import { motion } from "framer-motion";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import QuoteModal from "@/components/QuoteModal";
import usePageSEO from "@/hooks/usePageSEO";
import useOwnedPhotoSchemas from "@/hooks/useOwnedPhotoSchemas";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Star, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { getReviewsByTags } from "@/data/reviewData";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
const customHero = "/images/site/michigan-plank-rift-quartered-walnut-floor-dining-novi-mi.webp";
const customShopImg = "/images/site/michigan-plank-white-oak-sand-plymouth-mi.webp";
const customFiresideImg = "/images/site/michigan-plank-hickory-fireside-livonia-mi.webp";
import michiganPlankLogo from "@/assets/michigan-plank-flooring-logo.png";
import eleganceImg from "@/assets/1-wood-custom-line-8-inch-white-oak-plymouth-mi.webp";
import signatureImg from "@/assets/custom-8-inch-walnut-fas-northville-mi.webp";
const jumboImg = "/images/site/michigan-plank-european-white-oak-nature-novi-mi.webp";
import { PLANK_JOB_PHOTOS, PLANK_HUB_SHARE_IMAGES, makePlankHubImageSchemas, makePlankHubWebPageSchema } from "@/data/plankHubProjectImages";

const plankPhotoAssets: Record<string, string> = {
  "1-wood-custom-line-8-inch-white-oak-plymouth-mi": eleganceImg,
  "custom-8-inch-walnut-fas-northville-mi": signatureImg,
  "custom-nature-white-oak": jumboImg,
  "custom-fireside-hickory": customFiresideImg,
  "custom-sand-white-oak": customShopImg,
};
const plankSchemas = [
  ...makePlankHubImageSchemas("/michigan-plank-flooring", name => `https://1woodfloors.com${plankPhotoAssets[name]}`),
  makePlankHubWebPageSchema("/michigan-plank-flooring"),
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const europeanCollections = [
  {
    name: "Elegance Collection",
    description: "Our premium ready-to-customize flooring that delivers both efficiency and excellence. Every element is crafted from the same source lumber and finished with our proprietary process",
    specs: { species: "Character Grade European White Oak", width: "7 Inches", topLayer: "4mm Premium Hardwood", thickness: "5/8 or 3/4 Inch", colors: "12 Curated Colors or Custom" },
    img: eleganceImg,
  },
  {
    name: "Signature Collection",
    description: "Dramatic visual impact with extended-length boards and enhanced durability. The perfect balance of spectacular aesthetics and superior performance for generations",
    specs: { species: "Character or Prime Grade European White Oak", width: "7 or 10-1/4 Inches", topLayer: "6mm Premium Hardwood", lengths: "Up to 9 Foot 5  80% Full Length", colors: "12 Curated Colors or Custom" },
    img: signatureImg,
  },
  {
    name: "Jumbo Collection",
    description: "Statement-making wide planks that architects in Birmingham and Ann Arbor specify for grand entries and open floor plans. These are the boards that transform a room",
    specs: { species: "Character or Prime Grade European White Oak", width: "10  12  14  16  and 17-1/2 Inches", lengths: "7 Foot to 15 Foot", colors: "12 Curated Colors or Custom" },
    img: jumboImg,
  },
];

const domesticSpecies = [
  "Northern Red Oak", "Northern Maple", "Northern Walnut",
  "Northern Hickory", "Northern White Oak", "Northern Ash",
  "Rift & Quarter Sawn", "Nature Collection", "Specialty Collection",
];

const faqItems = [
  {
    q: "Is custom flooring actually more expensive than production brands",
    a: "Surprisingly, our collections are often more cost-effective than premium production lines. You are paying for raw, carefully crafted quality without the brand-name markup. The total project cost depends on your specific engineering requirements, but we consistently deliver luxury-grade results at a competitive price point.",
  },
  {
    q: "Where does the European Oak come from",
    a: "Our European White Oak is imported directly from elite mills in Europe, selected for tight grain structure and consistent grading. These long boards arrive as raw lumber and are custom-finished to your exact color specification. The result is a plank that no domestic production line can replicate.",
  },
  {
    q: "Can I choose a color that is not in your running line",
    a: "Absolutely. If our curated palette does not match your vision, our finishing lab will engineer a bespoke stain specifically for your project. We work with designers and architects daily to create one of a kind finishes that cannot be found anywhere else.",
  },
  {
    q: "Why do 96-inch lengths matter",
    a: "Standard production floors use 12 to 48-inch boards, creating a choppy, repetitive pattern. Our extended-length planks deliver uninterrupted visual continuity across large rooms, hallways, and open floor plans. It is the difference between a floor that looks installed and one that looks architectural.",
  },
  {
    q: "Can I specify the wear layer and core construction",
    a: "Yes. Our Solid Engineered construction features a sawn-face wear layer on a Baltic Birch core. This gives you the sanding life of traditional solid wood with the structural integrity required for Michigan's climate. We engineer custom core designs for specific subfloor conditions.",
  },
  {
    q: "Do you install custom floors across Michigan",
    a: "We do. While our Design Center is in Livonia, our collections are delivered and installed across the entire state. From the lakefronts of Charlevoix to the historic districts of Northville and Ann Arbor, we bring the showroom experience to your project site.",
  },
];

const CustomFlooring = () => {
  useOwnedPhotoSchemas(plankSchemas);
  usePageSEO({
    title: "Michigan Plank Flooring | Custom European White Oak Wide Plank",
    description:
      "Michigan Plank Flooring — our own-manufactured custom wide-plank European White Oak. Bespoke widths, finish systems, architectural integrations.",
    path: "/michigan-plank-flooring",
    image: PLANK_HUB_SHARE_IMAGES["/michigan-plank-flooring"],
    jsonLd: plankSchemas,
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <UtilBar />
      <SiteHeader />

      {/* ── HERO ── */}
      <section className="relative h-[85vh] min-h-[560px] max-h-[900px] flex items-end overflow-hidden">
        <img width={1920} height={1080} loading="eager" decoding="async" fetchPriority="high"
          src={customHero}
          alt={PLANK_JOB_PHOTOS[0].alt}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/30 to-transparent" />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 max-w-5xl mx-auto w-full px-6 md:px-12 pb-14 md:pb-20"
        >
          <PageBreadcrumb
            items={[
              { label: "Home", to: "/" },
              { label: "Michigan Plank Flooring" },
            ]}
          />
          <div className="flex items-center gap-4 mt-6 mb-3">
            <img width={200} height={60} src={michiganPlankLogo} alt="Michigan Plank Flooring logo" className="h-10 md:h-14 w-auto" loading="eager" />
            <p className="text-[10px] font-bold tracking-[.2em] uppercase text-primary-foreground/40">
              <span className="text-[hsl(var(--emerald-accent))]">Imported from Europe</span>
              &nbsp;&nbsp;Finished in Michigan
            </p>
          </div>
          <h1 className="font-display text-[clamp(34px,6vw,68px)] font-semibold text-primary-foreground leading-[1.02] tracking-tight mb-2">
            Michigan Plank Flooring
          </h1>
          <p className="font-display text-[clamp(18px,3vw,30px)] font-light text-primary-foreground/70 mb-6 leading-[1.2]">
            Custom Wide-Plank European White Oak — Imported Direct, Finished to Spec
          </p>
          <p className="text-[14px] md:text-[15px] font-light text-primary-foreground/50 leading-relaxed max-w-2xl mb-4">
            We import long-board European White Oak from elite mills overseas and custom-finish every plank to your exact specification. 30+ curated colors in extended lengths up to 96 inches. If the industry does not make the floor your design demands, we simply build it for you
          </p>
          <Link to="/projects/northville-walnut" className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[hsl(var(--gold-300))] hover:text-[hsl(var(--gold-200))] transition-colors mb-8">
            Featured: Northville FAS Walnut Masterpiece <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <div className="flex flex-col sm:flex-row gap-3">
            <QuoteModal
              trigger={
                <button className="bg-[hsl(var(--emerald-accent))] text-white px-7 py-3.5 rounded-full text-sm font-medium hover:brightness-110 transition-all text-center">
                  Schedule Showroom Visit
                </button>
              }
            />
            <a
              href="tel:2483258853"
              className="border border-primary-foreground/20 text-primary-foreground px-7 py-3.5 rounded-full text-sm font-medium hover:bg-primary-foreground/10 transition-colors text-center"
            >
              Call (248) 325-8853
            </a>
          </div>
        </motion.div>
      </section>

      {/* ── THE EUROPEAN OAK STORY ── */}
      <section className="bg-[hsl(var(--neutral-950))] py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="max-w-3xl mb-16">
            <p className="text-[10px] font-bold tracking-[.2em] uppercase text-[hsl(var(--emerald-accent))] mb-4 flex items-center gap-2.5">
              <span className="w-7 h-px bg-[hsl(var(--emerald-accent))]" />
              Why European White Oak
            </p>
            <h2 className="font-display text-[clamp(28px,4.5vw,52px)] font-semibold text-primary-foreground leading-[1.05] tracking-tight mb-6">
              Long Boards  Custom Colors<br className="hidden md:block" />
              <em className="italic font-light text-[hsl(var(--gold-300))]">Imported Directly</em>
            </h2>
            <p className="text-[14px] font-light text-primary-foreground/45 leading-[1.85] mb-4">
              European White Oak is the gold standard of modern hardwood flooring. Its tight, consistent grain structure accepts custom stains with a depth and uniformity that domestic oak simply cannot match. The lumber arrives from elite European mills in lengths up to 96 inches, delivering the seamless, architectural flow that short production boards can never achieve
            </p>
            <p className="text-[14px] font-light text-primary-foreground/45 leading-[1.85]">
              Every plank is custom-finished in our process to your exact color specification. From warm honey tones favored in Birmingham to the cool contemporary greys popular with Ann Arbor architects, we build the exact color your design demands. If our 30+ curated palette does not hit the mark, our finishing lab will engineer a bespoke stain specifically for your project
            </p>
          </motion.div>

          {/* Signature Specs Row */}
          <motion.div {...fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-px rounded-[20px] overflow-hidden">
            {[
              { label: "Width", value: "Up to 17.5\"", sub: "Wide Plank" },
              { label: "Length", value: "Up to 96\"", sub: "Extended Boards" },
              { label: "Colors", value: "30+", sub: "Curated or Bespoke" },
              { label: "Construction", value: "Solid Eng", sub: "Baltic Birch Core" },
            ].map((s) => (
              <div key={s.label} className="bg-primary-foreground/[.04] p-6 md:p-8 text-center">
                <p className="text-[10px] font-bold tracking-[.18em] uppercase text-primary-foreground/30 mb-2">{s.label}</p>
                <p className="font-display text-2xl md:text-3xl font-semibold text-primary-foreground mb-1">{s.value}</p>
                <p className="text-[11px] text-primary-foreground/35">{s.sub}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── EUROPEAN COLLECTIONS ── */}
      <section className="bg-[hsl(var(--neutral-950))] pb-20 md:pb-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-12">
            <p className="text-[10px] font-bold tracking-[.2em] uppercase text-primary mb-3 flex items-center gap-2.5">
              <span className="w-7 h-px bg-primary" />
              European White Oak Collections
            </p>
            <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold text-primary-foreground leading-[1.05]">
              Three Tiers of <em className="italic font-light text-[hsl(var(--gold-300))]">Custom Excellence</em>
            </h2>
          </motion.div>

          <div className="flex flex-col gap-px rounded-[20px] overflow-hidden">
            {europeanCollections.map((col, i) => (
              <motion.div
                key={col.name}
                {...fadeUp}
                className={`grid md:grid-cols-2 gap-0 bg-primary-foreground/[.03] ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}
              >
                <figure className="min-w-0 flex flex-col [direction:ltr]">
                  <div className="aspect-[16/10] md:aspect-auto md:flex-1 overflow-hidden">
                  <img width={800} height={534} loading="lazy"
                    src={col.img}
                    alt={PLANK_JOB_PHOTOS[i + 1].alt}
                    className="w-full h-full object-cover grayscale-[20%] contrast-[1.1] hover:grayscale-0 hover:scale-[1.04] transition-all duration-700"
                  />
                  </div>
                  <figcaption className="px-4 py-3 text-xs leading-relaxed text-primary-foreground/70">{PLANK_JOB_PHOTOS[i + 1].caption}</figcaption>
                </figure>
                <div className="p-8 md:p-10 lg:p-12 flex flex-col justify-center [direction:ltr]">
                  <h3 className="font-display text-[clamp(20px,2.5vw,28px)] font-semibold text-primary-foreground mb-3">{col.name}</h3>
                  <p className="text-[13px] text-primary-foreground/40 leading-relaxed mb-5">{col.description}</p>
                  <div className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-1.5 text-[12px]">
                    {Object.entries(col.specs).map(([key, val]) => (
                      <span key={key} className="contents">
                        <span className="text-primary-foreground/25 capitalize">{key.replace(/([A-Z])/g, " $1").trim()}</span>
                        <span className="text-primary-foreground/55">{val}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="mt-4 text-xs text-primary-foreground/70">See Michigan Plank walnut installed: <Link to="/projects/birmingham-rift-quartered-walnut" className="text-primary underline underline-offset-4">rift and quartered walnut in Birmingham, MI</Link></p>
        </div>
      </section>

      {/* ── CUSTOM SHOP: REAL PROJECTS ── */}
      <section className="bg-background py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-12">
            <p className="text-[10px] font-bold tracking-[.2em] uppercase text-[hsl(var(--emerald-accent))] mb-3 flex items-center gap-2.5">
              <span className="w-7 h-px bg-[hsl(var(--emerald-accent))]" />
              The Custom Shop
            </p>
            <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold text-foreground leading-[1.05]">
              Your Species  Your Width  Your{" "}
              <em className="italic font-light text-primary">Color</em>
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl mt-4">
              When our curated collections do not capture your vision, we go further. Custom species, custom widths, custom colors. We will travel to the factory floor to build it right
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-1 rounded-[20px] overflow-hidden mb-1">
            <figure className="min-w-0">
            <Link to="/projects/livonia-subfloor" className="group relative aspect-[4/3] overflow-hidden block">
              <img width={800} height={534} loading="lazy"
                src={customFiresideImg}
                alt={PLANK_JOB_PHOTOS[4].alt}
                className="w-full h-full object-cover grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.04] transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="font-display text-lg font-semibold text-white mb-1">8" Hickory · Fireside</p>
                <p className="text-[11px] text-white/50 leading-relaxed max-w-sm mb-3">
                  Custom color developed for a client in Livonia. 4mm sawn face on 3/4" Baltic Birch relief-cut core. Edmond personally traveled to Wisconsin to develop and finish this color
                </p>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[hsl(var(--gold-300))] group-hover:text-[hsl(var(--gold-200))] transition-colors">
                  View Project <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
            <figcaption className="px-4 py-3 text-xs leading-relaxed text-muted-foreground">{PLANK_JOB_PHOTOS[4].caption}</figcaption>
            </figure>
            <figure className="min-w-0">
            <Link to="/projects/plymouth-custom-white-oak" className="group relative aspect-[4/3] overflow-hidden block">
              <img width={800} height={534} loading="lazy"
                src={customShopImg}
                alt={PLANK_JOB_PHOTOS[5].alt}
                className="w-full h-full object-cover grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.04] transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="font-display text-lg font-semibold text-white mb-1">8" White Oak · Sand</p>
                <p className="text-[11px] text-white/50 leading-relaxed max-w-sm mb-3">
                  Engineered 4mm sawn face on 3/4" Baltic Birch core. Character grade White Oak with our signature Sand finish
                </p>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[hsl(var(--gold-300))] group-hover:text-[hsl(var(--gold-200))] transition-colors">
                  View Project <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
            <figcaption className="px-4 py-3 text-xs leading-relaxed text-muted-foreground">{PLANK_JOB_PHOTOS[5].caption}</figcaption>
            </figure>
          </div>

          {/* Domestic Species  clean horizontal list */}
          <motion.div {...fadeUp} className="bg-foreground rounded-[20px] p-8 md:p-10 mt-1">
            <div className="flex flex-col md:flex-row md:items-center gap-5">
              <div className="md:max-w-xs shrink-0">
                <p className="text-[10px] font-bold tracking-[.18em] uppercase text-primary-foreground/30 mb-2">Domestic Species</p>
                <h3 className="font-display text-xl font-semibold text-primary-foreground leading-tight">
                  Michigan-Sourced Hardwoods
                </h3>
                <p className="text-[12px] text-primary-foreground/35 mt-2 leading-relaxed">
                  Select lumber sourced from Grand Rapids and finished to the same exacting standards as our European line
                </p>
              </div>
              <div className="flex flex-wrap gap-2 flex-1">
                {domesticSpecies.map((species) => (
                  <span
                    key={species}
                    className="border border-primary-foreground/10 rounded-full px-4 py-2 text-[11px] font-medium text-primary-foreground/60 hover:border-primary-foreground/25 hover:text-primary-foreground/80 transition-colors"
                  >
                    {species}
                  </span>
                ))}
              </div>
            </div>
            <div className="mt-6 pt-5 border-t border-primary-foreground/8">
              <p className="text-[12px] text-primary-foreground/30 leading-relaxed">
                Available in Solid and Engineered formats. Red Oak  White Oak  Ash  Maple  Hickory  American Walnut in widths from 2-1/4 to 10 inches with Character or Select grading. 4mm or 6mm sawn-face wear layer options
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FORENSIC ADVANTAGE ── */}
      <section className="bg-foreground py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-12">
            <p className="text-[10px] font-bold tracking-[.2em] uppercase text-[hsl(var(--emerald-accent))] mb-3 flex items-center gap-2.5">
              <span className="w-7 h-px bg-[hsl(var(--emerald-accent))]" />
              The Forensic Advantage
            </p>
            <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold text-primary-foreground leading-[1.05]">
              Break the <em className="italic font-light text-[hsl(var(--gold-300))]">Pre-Finished Ceiling</em>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-px rounded-[20px] overflow-hidden">
            {[
              {
                title: "Your Species  Your Rules",
                text: "European Oak is our flagship, but we custom-source White Oak, FAS Walnut, and specialty species to match any architectural vision. If it grows and it is beautiful, we can build a floor from it",
              },
              {
                title: "Engineered for Michigan",
                text: "Our Solid Engineered construction with a Baltic Birch core is specifically designed for the dramatic humidity swings of a Michigan home. Real-wood sanding life that LVP and thin-veneer products cannot offer",
              },
              {
                title: "Finished in Michigan",
                text: "We do not just import and install. We custom-finish flooring to your exact architectural specifications. The texture of a wire-brushed finish or the depth of a hand-rubbed oil is something no screen can capture",
              },
            ].map((card) => (
              <motion.div key={card.title} {...fadeUp} className="bg-primary-foreground/[.04] p-8 md:p-10">
                <h3 className="font-display text-lg font-semibold text-primary-foreground mb-3">{card.title}</h3>
                <p className="text-[13px] text-primary-foreground/35 leading-relaxed">{card.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="text-center mt-10">
            <p className="text-[13px] text-primary-foreground/40 leading-relaxed max-w-2xl mx-auto mb-6">
              Surprisingly, this bespoke route is often more cost-effective than standard high-end production lines. You are not paying for brand-name markup. You are paying for raw, carefully crafted quality
            </p>
            <QuoteModal
              trigger={
                <button className="bg-[hsl(var(--emerald-accent))] text-white px-8 py-3.5 rounded-full text-sm font-medium hover:brightness-110 transition-all">
                  Schedule Showroom Visit
                </button>
              }
            />
          </motion.div>
        </div>
      </section>

      {/* ── REVIEWS ── */}
      <GoogleReviewsCarousel contextTags={["custom-line", "product-specification", "installation"]} count={4} />

      {/* ── FAQ ── */}
      <section className="bg-foreground py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-8">
            <p className="text-[10px] font-bold tracking-[.2em] uppercase text-[hsl(var(--emerald-accent))] mb-3">
              Common Questions
            </p>
            <h2 className="font-display text-[clamp(24px,4vw,40px)] font-semibold text-primary-foreground leading-[1.05]">
              The Custom Flooring FAQ
            </h2>
          </motion.div>

          <motion.div {...fadeUp}>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-primary-foreground/10">
                  <AccordionTrigger className="text-left text-[hsl(var(--emerald-accent))] hover:no-underline text-sm md:text-base font-medium py-5">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[13px] text-primary-foreground/45 leading-relaxed pb-5">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          <motion.div {...fadeUp} className="mt-10 text-center">
            <QuoteModal
              trigger={
                <button className="bg-[hsl(var(--emerald-accent))] text-white px-8 py-3.5 rounded-full text-sm font-medium hover:brightness-110 transition-all">
                  Schedule Showroom Visit
                </button>
              }
            />
          </motion.div>

          <motion.div {...fadeUp} className="mt-8 text-center">
            <p className="text-[13px] text-primary-foreground/40 leading-relaxed">
              Browse the full Michigan Plank Flooring collection at{" "}
              <a
                href="https://michiganplankflooring.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[hsl(var(--gold-300))] hover:text-[hsl(var(--gold-200))] underline underline-offset-2 transition-colors"
              >
                michiganplankflooring.com
              </a>
            </p>
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default CustomFlooring;
